/** Choosing when, how many and which car, with a price for each from the firm's rate card. */

import { errorMessage, post } from "@taxi/mobile-web/api";
import { dayLabel, fromUkLocal, minutes, money, time, ukDate } from "@taxi/mobile-web/format";
import { BagIcon, UsersIcon } from "@taxi/mobile-web/icons";
import { MapView } from "@taxi/mobile-web/map";
import { navigate } from "@taxi/mobile-web/router";
import { Button, Notice, Segmented, Stepper, TopBar } from "@taxi/mobile-web/ui";
import { useEffect, useMemo, useState } from "react";
import { VehicleArt } from "../components/vehicle-art";
import { placeTitle, useDraft, type When } from "../lib/draft";
import { useSession } from "../lib/session";
import type { Place, Quote } from "../lib/types";

const placeInput = (p: Place) => ({
  address: p.address,
  postcode: p.postcode,
  lat: p.lat,
  lng: p.lng,
  name: p.name,
});

/** The pickup time a "when" means, or undefined for now. */
export function pickupAtFor(when: When): string | undefined {
  return when.asap ? undefined : fromUkLocal(when.date, when.time).toISOString();
}

function defaultLater(now = new Date()): When {
  const at = new Date(Math.ceil((now.getTime() + 60 * 60_000) / (15 * 60_000)) * 15 * 60_000);
  return { asap: false, date: ukDate(at), time: time(at) };
}

export function QuotesScreen() {
  const { firm } = useSession();
  const { draft, update } = useDraft();
  const [state, setState] = useState<{ loading: boolean; error: string | null }>({
    loading: draft.quotes.length === 0,
    error: null,
  });
  const stops = draft.stops.filter((s) => s.address);

  useEffect(() => {
    if (!draft.pickup || !draft.dropoff) navigate("/book", { replace: true });
  }, [draft.pickup, draft.dropoff]);

  const pickupAt = pickupAtFor(draft.when);
  // biome-ignore lint/correctness/useExhaustiveDependencies: priced again only when the trip changes.
  const request = useMemo(
    () =>
      draft.pickup && draft.dropoff
        ? {
            pickup: placeInput(draft.pickup),
            dropoff: placeInput(draft.dropoff),
            stops: stops.map(placeInput),
            passengers: draft.passengers,
            ...(pickupAt ? { pickupAt } : {}),
          }
        : null,
    [JSON.stringify([draft.pickup, draft.dropoff, stops, draft.passengers, pickupAt])],
  );

  // biome-ignore lint/correctness/useExhaustiveDependencies: only when what's being priced changes.
  useEffect(() => {
    if (!request) return;
    // Always a fresh price; the last ones stay on screen meanwhile.
    let cancelled = false;
    setState((s) => ({ ...s, loading: true, error: null }));
    post<{ data: Quote[] }>("/customer/quotes", request)
      .then(({ data }) => {
        if (cancelled) return;
        const chosenType = draft.quotes.find((d) => d.id === draft.quoteId)?.vehicleType.id;
        const keep = data.find((q) => q.vehicleType.id === chosenType);
        update({ quotes: data, quoteId: (keep ?? data[0])?.id ?? null });
        setState({ loading: false, error: null });
      })
      .catch((error) => {
        if (cancelled) return;
        update({ quotes: [], quoteId: null });
        setState({ loading: false, error: errorMessage(error) });
      });
    return () => {
      cancelled = true;
    };
  }, [request]);

  if (!draft.pickup || !draft.dropoff) return null;
  const selected = draft.quotes.find((q) => q.id === draft.quoteId) ?? null;
  const today = new Date();
  const days = Array.from({ length: Math.min(firm.booking.maxDaysAhead, 60) + 1 }, (_, i) => {
    const day = new Date(today.getTime() + i * 86_400_000);
    return { value: ukDate(day), label: dayLabel(day, today) };
  });

  return (
    <div className="quotes">
      <TopBar title="Choose a ride" back="/book" transparent />
      <MapView
        pickup={draft.pickup}
        dropoff={draft.dropoff}
        stops={stops}
        className="quotes-map"
        bottomInset={24}
        labels={{
          pickup: placeTitle(draft.pickup),
          dropoff: (
            <>
              {placeTitle(draft.dropoff)}
              {selected && <small>{minutes(selected.durationSeconds)} drive</small>}
            </>
          ),
        }}
      />
      <div className="quotes-sheet">
        <button type="button" className="trip-summary" onClick={() => navigate("/book")}>
          <span className="trip-summary-places">
            <span>
              <i className="route-dot route-dot-pickup" /> {placeTitle(draft.pickup)}
            </span>
            {stops.map((stop) => (
              <span key={`${stop.lat},${stop.lng}`}>
                <i className="route-dot route-dot-stop" /> {placeTitle(stop)}
              </span>
            ))}
            <span>
              <i className="route-dot route-dot-dropoff" /> {placeTitle(draft.dropoff)}
            </span>
          </span>
          <span className="pill">Edit</span>
        </button>

        <div className="quotes-options">
          <Segmented
            label="When"
            value={draft.when.asap ? "now" : "later"}
            options={[
              { value: "now", label: "Now" },
              { value: "later", label: "Later" },
            ]}
            onChange={(value) =>
              update({ when: value === "now" ? { asap: true } : defaultLater() })
            }
          />
          {!draft.when.asap && (
            <div className="when-pickers">
              <label>
                <span className="sr-only">Day</span>
                <select
                  value={draft.when.date}
                  onChange={(e) =>
                    update({
                      when: {
                        ...(draft.when as Extract<When, { asap: false }>),
                        date: e.target.value,
                      },
                    })
                  }
                >
                  {days.map((d) => (
                    <option key={d.value} value={d.value}>
                      {d.label}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                <span className="sr-only">Time (UK)</span>
                <input
                  type="time"
                  step={300}
                  value={draft.when.time}
                  onChange={(e) =>
                    e.target.value &&
                    update({
                      when: {
                        ...(draft.when as Extract<When, { asap: false }>),
                        time: e.target.value,
                      },
                    })
                  }
                />
              </label>
            </div>
          )}
          <Stepper
            label="Passengers"
            icon={<UsersIcon size={20} />}
            value={draft.passengers}
            min={1}
            max={firm.booking.maxPassengers}
            onChange={(passengers) => update({ passengers })}
          />
        </div>

        {state.error && <Notice tone="error">{state.error}</Notice>}
        <ul className="quote-list" aria-busy={state.loading}>
          {state.loading && draft.quotes.length === 0
            ? [0, 1, 2].map((i) => <li key={i} className="quote quote-skeleton" />)
            : draft.quotes.map((quote) => {
                const type = firm.vehicleTypes.find((t) => t.id === quote.vehicleType.id);
                const active = quote.id === draft.quoteId;
                return (
                  <li key={quote.id}>
                    <button
                      type="button"
                      className={`quote${active ? " active" : ""}${state.loading ? " stale" : ""}`}
                      aria-pressed={active}
                      onClick={() => update({ quoteId: quote.id })}
                    >
                      <span className="quote-art">
                        <VehicleArt icon={quote.vehicleType.icon} size={80} />
                      </span>
                      <span className="quote-text">
                        <strong>{quote.vehicleType.name}</strong>
                        <span className="meta">
                          <span>
                            <UsersIcon size={14} /> {quote.vehicleType.seats}
                          </span>
                          {type && type.luggage > 0 && (
                            <span>
                              <BagIcon size={14} /> {type.luggage}
                            </span>
                          )}
                        </span>
                      </span>
                      <span className="quote-right">
                        <span className="quote-price">
                          {money(quote.pricePence)}
                          {quote.isEstimate && <small>estimate</small>}
                        </span>
                        <span className="quote-eta">
                          {quote.isAsap
                            ? quote.etaSeconds !== null
                              ? `${minutes(quote.etaSeconds)} away`
                              : ""
                            : time(new Date(quote.pickupAt))}
                        </span>
                      </span>
                    </button>
                  </li>
                );
              })}
        </ul>
        {selected?.isEstimate && (
          <p className="fine-print">
            Fares are metered: you'll pay what the journey actually costs.
          </p>
        )}
      </div>

      <div className="footer-bar">
        <Button
          disabled={!selected || state.loading}
          onClick={() => navigate("/book/confirm")}
          value={selected ? money(selected.pricePence) : undefined}
        >
          {selected ? `Choose ${selected.vehicleType.name}` : "Choose a ride"}
        </Button>
      </div>
    </div>
  );
}
